import { create } from 'zustand'
import { useFeatureFlagsStore } from '@renderer/stores/featureFlagsStore'

/**
 * Camera Rest — connection-preserving camera sleep ("park").
 *
 * A parked camera keeps its USB claim, WebUSB grant, and WASM context alive;
 * only liveview stops (best-effort `viewfinder=0` drops the mirror). Waking is
 * therefore instant and gesture-free — unlike the legacy idle-eject, whose full
 * disconnect forces the reconnect through `navigator.usb.requestDevice()` and
 * its user-activation requirement (the "Click to Start Camera" prompt).
 *
 * This store holds ONLY the parked flags so React components (Preview) can
 * subscribe without importing uniCamUtils (which itself imports this store —
 * keeping the dependency one-way avoids a cycle). All park/wake ORCHESTRATION
 * lives in `unifiedCameraUtils.parkCamera/wakeCamera`; nothing else should
 * write here. Deliberately NOT persisted: a fresh renderer boot always starts
 * un-parked, matching the camera's real state after a reload.
 *
 * Full design + rollout plan: docs/camera-rest-plan.md.
 */

type CameraKey = 'primary' | 'secondary'

/**
 * Master gate for ALL camera-rest behavior, registered in the Studio flag
 * registry (config/featureFlags.ts, surface "app"). OFF ⇒ the camera simply
 * never rests: no idle park, no parked guards. The legacy alternative it once
 * fell back to — idle-EJECT behind the operator's "Eject Camera When Idle
 * (Experimental)" toggle — has been removed; a full disconnect forced every
 * wake through `navigator.usb.requestDevice()` and its user gesture, which is
 * the whole reason CameraPrompt existed on the idle path.
 */
export const CAMERA_REST_FLAG = 'camera_rest'

export function isCameraRestEnabled(): boolean {
  return useFeatureFlagsStore.getState().getFlag(CAMERA_REST_FLAG, false)
}

interface CameraRestState {
  parked: Record<CameraKey, boolean>
  setParked: (cameraKey: CameraKey, parked: boolean) => void
  isParked: (cameraKey: CameraKey) => boolean
  isAnyParked: () => boolean
}

export const useCameraRestStore = create<CameraRestState>()((set, get) => ({
  parked: { primary: false, secondary: false },
  setParked: (cameraKey: CameraKey, parked: boolean): void =>
    set((state) => ({ parked: { ...state.parked, [cameraKey]: parked } })),
  isParked: (cameraKey: CameraKey): boolean => get().parked[cameraKey],
  isAnyParked: (): boolean => get().parked.primary || get().parked.secondary
}))

/**
 * Live-preview consumers per camera — how many <Preview> components are mounted
 * and pulling frames RIGHT NOW. Counted unconditionally (every Preview mount,
 * no feature flag), because "a guest is looking at the liveview" is a fact about
 * the screen, not about a rollout.
 *
 * Kept as a plain module record rather than zustand state: it is read only by
 * park guards (imperative, not render-driven), and lives HERE — the leaf store
 * with no camera imports — so `uniCamUtils` can consult it without the cycle a
 * `previewRefcount` import would create (previewRefcount imports uniCamUtils).
 *
 * The invariant every park authority must honour: NEVER park a camera whose
 * liveview is on screen. Parking stops the pull loop, so the guest is left
 * staring at a frozen frame until the next touch — the visible bug that
 * single-page booths (bahela-receipt/bahela-quick payment + countdown, all the
 * newspaper Welcome screens) hit while nobody touches the panel.
 */
const previewConsumers: Record<CameraKey, number> = { primary: 0, secondary: 0 }

/** Register a mounted live preview for `cameraKey`. Returns the release fn. */
export function addPreviewConsumer(cameraKey: CameraKey): () => void {
  previewConsumers[cameraKey] += 1
  let released = false
  return (): void => {
    if (released) return
    released = true
    previewConsumers[cameraKey] = Math.max(0, previewConsumers[cameraKey] - 1)
  }
}

/** Is any live preview currently mounted for this camera? */
export function hasPreviewConsumers(cameraKey: CameraKey): boolean {
  return previewConsumers[cameraKey] > 0
}

/** Is any live preview currently mounted for EITHER camera? */
export function hasAnyPreviewConsumers(): boolean {
  return previewConsumers.primary > 0 || previewConsumers.secondary > 0
}
